import { DEVICE_TYPES } from '../constants/websheetsConstants';

export type KeyOf<T = unknown> = keyof T;
export type Maybe<T = unknown> = T | null;
export type Optional<T = unknown> = T | undefined;
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};
export type PropsWithClassName<P = unknown> = P & {
  className?: string | undefined;
};

export type SilentParam<T = unknown> = T & {
  silent?: boolean;
};

export type FlexibleObject<T = any> = {
  [key: string]: T;
};

/**
 * Bypasses TypeScript type checking by casting a value to `any`.
 * Useful in test scenarios where you need to intentionally pass invalid types
 * to a function that has strict type constraints, while keeping the original
 * function's type safety intact.
 */
export const bypassType = <T>(value: T): any => value;

export const appTypes = {
  portal: 'portal',
  websheet: 'websheet',
} as const;

export type MonthValue<T = unknown> = T & {
  month: string;
  value: number;
};

export type IdLabelType = {
  id: string;
  label: string;
};

export type ValueLabelType = {
  value: string;
  label: string;
};

export type CustomIconsType = {
  size: number;
  fill?: string;
};

export type SignOutType = Partial<{
  global: boolean;
  appMsg: any;
  forceSignOut: boolean;
}>;

export interface TextEditorContent {
  type: string;
  children?: ({ text: string } | TextEditorContent)[];
}

export type PlatformType = (typeof DEVICE_TYPES)[keyof typeof DEVICE_TYPES];

export type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

export type ResponsiveValue<T> = T | Partial<Record<Breakpoint, T>>;

export const isResponsive = <T>(
  value: ResponsiveValue<T>,
): value is Partial<Record<Breakpoint, T>> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

export interface HttpErrorExtras {
  /**
   * Backend-authored copy that is safe to show to the user. Only HTTP
   * transports set it, and only for 4xx responses carrying a `detail`.
   * `message` stays the raw transport text for logs and classifiers.
   */
  userMessage?: string;
  /** Backend `error_code`, when the response body carries one. */
  code?: string;
  /** Backend `error_id`, for support correlation. */
  errorId?: string;
}

export class HttpError extends Error {
  statusCode?: number;
  userMessage?: string;
  code?: string;
  errorId?: string;

  constructor(
    message: string,
    statusCode?: number,
    extras: HttpErrorExtras = {},
  ) {
    super(message);
    this.name = 'HttpError';
    // Required: fixes instanceof checks when transpiling to ES5
    Object.setPrototypeOf(this, new.target.prototype);
    if (statusCode !== undefined) {
      this.statusCode = statusCode;
    }
    if (extras.userMessage !== undefined) {
      this.userMessage = extras.userMessage;
    }
    if (extras.code !== undefined) {
      this.code = extras.code;
    }
    if (extras.errorId !== undefined) {
      this.errorId = extras.errorId;
    }
  }
}
